/**
 * Словари значений из раздела 5 спецификации.
 *
 * Списки закрытые — их проверяет база. Подписи собраны здесь, чтобы одно и
 * то же значение не называлось на двух экранах по-разному.
 */

import type {
  Band,
  CallStatus,
  CtMatchKind,
  CtNumberState,
  DeliveryStatus,
  DepartmentSuggestionStatus,
  Direction,
  Disposition,
  EmployeeStatus,
  GrantRole,
  IntegrationAuthType,
  IntegrationStatus,
  OutboundEventType,
  PoolSharing,
  MissedActionKind,
  MissedReason,
  MissedState,
  Reached,
  RecoveryKind,
  RuleResult,
  RuleType,
  SourceKind,
  SourceStatus,
  UnitKind,
  UnitStatus,
} from './api/types';

/** Тон бейджа. `neutral` — «не сбой и не успех», отдельный вид. */
export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

export interface Term {
  label: string;
  tone: Tone;
  hint?: string;
}

/**
 * Статус обработки.
 *
 * `skipped` — нейтральный намеренно: это пропущенный звонок, главная метрика
 * колл-центра, а не сбой конвейера. Красный здесь искажает отчёт.
 */
export const callStatus: Record<CallStatus, Term> = {
  pending: { label: 'В очереди', tone: 'muted', hint: 'Принят, ждёт обработки' },
  awaiting_audio: {
    label: 'Ждёт запись',
    tone: 'muted',
    hint: 'Метаданные есть, запись ещё не загружена',
  },
  processing: { label: 'Обрабатывается', tone: 'info' },
  done: { label: 'Готово', tone: 'success' },
  error: { label: 'Ошибка', tone: 'danger', hint: 'Обработка не удалась' },
  skipped: {
    label: 'Без обработки',
    tone: 'neutral',
    hint: 'Записи нет и не будет — обработка не требуется',
  },
};

/** Телефонный исход. Измерение, независимое от статуса обработки. */
export const disposition: Record<Disposition, Term> = {
  answered: { label: 'Отвечен', tone: 'success' },
  no_answer: { label: 'Не отвечен', tone: 'warning' },
  abandoned: { label: 'Брошен', tone: 'warning', hint: 'Клиент положил трубку до ответа' },
  busy: { label: 'Занято', tone: 'neutral' },
  voicemail: { label: 'Автоответчик', tone: 'neutral' },
  failed: { label: 'Не состоялся', tone: 'danger' },
  cancelled: { label: 'Отменён', tone: 'neutral' },
  unknown: { label: 'Неизвестно', tone: 'muted' },
};

export const direction: Record<Direction, Term> = {
  inbound: { label: 'Входящий', tone: 'info' },
  outbound: { label: 'Исходящий', tone: 'neutral' },
  internal: { label: 'Внутренний', tone: 'muted' },
};

/**
 * Полосы балла. Пороги здесь не дублируются: полосу считает сервер, а
 * критическое нарушение красит её красной при любом балле.
 */
export const band: Record<Band, Term> = {
  '': { label: 'Не оценивался', tone: 'muted', hint: 'Ни одно правило не смогло примениться' },
  green: { label: 'Хорошо', tone: 'success' },
  yellow: { label: 'Приемлемо', tone: 'warning' },
  orange: { label: 'Слабо', tone: 'warning' },
  red: { label: 'Плохо', tone: 'danger' },
};

/**
 * Заливка шкалы балла по полосе.
 *
 * Отдельная таблица нужна потому, что шкала — сплошной цвет, а не бейдж с
 * рамкой. Порогов здесь по-прежнему нет: цвет берётся из `band`.
 */
export const bandBarClass: Record<Band, string> = {
  '': 'bg-slate-200',
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
};

/**
 * Виды перевода — словами станции.
 *
 * Разные виды намеренно не сводятся в один «перевод»: слепой перевод и
 * перевод с сопровождением — разные действия оператора, и различать их
 * важнее, чем упрощать подпись. Незнакомое значение показываем как есть.
 */
export const transferKind: Record<string, string> = {
  bl_xfer: 'слепой перевод',
  att_xfer: 'перевод с сопровождением',
  uuid_br: 'соединение каналов',
};

/** Цвет кольца оценки. Тоже по полосе — порогов на клиенте нет. */
/**
 * Цвет **текста** балла в плотных таблицах.
 *
 * Отдельная таблица, потому что бейдж и текст красятся по-разному: у бейджа
 * есть фон, и он тянет на себя внимание в каждой строке, а в списке важнее
 * видеть строки, где балл плохой. Текст даёт ту же полосу без веса.
 *
 * Порогов здесь по-прежнему нет: цвет берётся из `band`, который считает
 * сервер. Провал критического правила красит красным балл 80 — и так и надо.
 */
export const bandTextClass: Record<Band, string> = {
  '': 'text-slate-300',
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  orange: 'text-orange-600 dark:text-orange-400',
  red: 'text-red-600 dark:text-red-400',
};

export const bandStroke: Record<Band, string> = {
  '': '#cbd5e1',
  green: '#22c55e',
  yellow: '#eab308',
  orange: '#f97316',
  red: '#ef4444',
};

/**
 * Решение по разговору другого отдела.
 *
 * Подписи говорят про работу, а не про перенос: «подтверждено» значит «да,
 * это сделал он», а звонок при любом решении остаётся за отделом сотрудника.
 */
export const departmentSuggestionStatus: Record<DepartmentSuggestionStatus, Term> = {
  pending: { label: 'Не рассмотрено', tone: 'warning', hint: 'Руководитель ещё не решил' },
  accepted: { label: 'Подтверждено', tone: 'success', hint: 'Работу другого отдела сделал он' },
  rejected: { label: 'Отклонено', tone: 'muted', hint: 'Разговор всё-таки своего отдела' },
};

/** Результат правила. `skipped` — не провал: правило не смогло примениться. */
export const ruleResult: Record<RuleResult, Term> = {
  pass: { label: 'Выполнено', tone: 'success' },
  fail: { label: 'Не выполнено', tone: 'danger' },
  skipped: { label: 'Не применимо', tone: 'muted', hint: 'Правило не смогло примениться' },
  error: { label: 'Сбой правила', tone: 'danger' },
};

/**
 * Типы правил. Три из пяти заводятся, но всегда возвращают `skipped` —
 * в редакторе их показываем с пометкой, а не прячем.
 */
export const ruleType: Record<RuleType, Term & { implemented: boolean }> = {
  keyword: { label: 'Ключевые слова', tone: 'neutral', implemented: true },
  duration: { label: 'Длительность', tone: 'neutral', implemented: true },
  sentiment: { label: 'Тональность', tone: 'muted', implemented: false, hint: 'Пока не считается' },
  ai: {
    label: 'Вопрос модели',
    tone: 'neutral',
    implemented: true,
    hint: 'Вердикт даёт модель; если она не ответила, правило вернёт «не применимо»',
  },
  silence: { label: 'Паузы', tone: 'muted', implemented: false, hint: 'Пока не считается' },
};

export const grantRole: Record<GrantRole, Term> = {
  tenant_admin: { label: 'Администратор тенанта', tone: 'info' },
  unit_admin: { label: 'Администратор подразделения', tone: 'info' },
  supervisor: { label: 'Руководитель', tone: 'neutral' },
  analyst: { label: 'Аналитик', tone: 'neutral' },
  agent: { label: 'Оператор', tone: 'muted' },
};

export const unitKind: Record<UnitKind, Term> = {
  root: { label: 'Компания', tone: 'neutral' },
  region: { label: 'Регион', tone: 'neutral' },
  branch: { label: 'Филиал', tone: 'neutral' },
  department: { label: 'Отдел', tone: 'neutral' },
};

export const unitStatus: Record<UnitStatus, Term> = {
  active: { label: 'Активен', tone: 'success' },
  archived: { label: 'В архиве', tone: 'muted' },
};

export const sourceKind: Record<SourceKind, Term> = {
  cloud_pbx: { label: 'Облачная АТС', tone: 'neutral' },
  external_api: { label: 'Внешний API', tone: 'neutral' },
  manual: { label: 'Ручная загрузка', tone: 'muted' },
};

export const sourceStatus: Record<SourceStatus, Term> = {
  active: { label: 'Активен', tone: 'success' },
  paused: { label: 'Приостановлен', tone: 'warning' },
  disabled: { label: 'Отключён', tone: 'muted' },
};

export const employeeStatus: Record<EmployeeStatus, Term> = {
  active: { label: 'Работает', tone: 'success' },
  inactive: { label: 'Не работает', tone: 'muted' },
  on_leave: { label: 'В отпуске', tone: 'warning' },
};

/**
 * Подпись неизвестного значения.
 *
 * Бэкенд может завести новое значение раньше, чем фронт про него узнает.
 * Показать сырое значение честнее, чем пустоту: по нему хотя бы понятно,
 * что произошло.
 */
export function term<T extends string>(dict: Record<T, Term>, value: T | string): Term {
  return (dict as Record<string, Term>)[value] ?? { label: value, tone: 'muted' };
}

/**
 * Стадия вызова — по-человечески.
 *
 * Третье измерение рядом с исходом и статусом: исход отвечает «чем
 * кончилось», стадия — «где оборвалось». Слова выбраны так, чтобы читались
 * без объяснений: не «ivr», а «ответила станция».
 *
 * `operator` нейтральный, а не зелёный: соединили с человеком — это норма, а
 * не достижение, и красить норму значит обесценивать цвет там, где он нужен.
 */
export const reached: Record<Reached, Term> = {
  unknown: { label: 'Неизвестно', tone: 'muted', hint: 'Разобрать стадию не вышло' },
  none: {
    label: 'Не сняли трубку',
    tone: 'warning',
    hint: 'Станция даже не ответила на вызов',
  },
  ivr: {
    label: 'Ответила станция',
    tone: 'neutral',
    hint: 'Приветствие или меню; до набора добавочных дело не дошло',
  },
  ring: {
    label: 'Звенели добавочные',
    tone: 'neutral',
    hint: 'Вызов дошёл до телефонов, трубку не сняли',
  },
  voicemail: { label: 'Голосовая почта', tone: 'neutral' },
  operator: {
    label: 'Соединили с оператором',
    tone: 'neutral',
    hint: 'Только это и делает звонок отвеченным',
  },
};

/** Состояние пропущенного. «Потеряно» считает сервер — здесь только слова. */
export const missedState: Record<MissedState, Term> = {
  open: { label: 'В работе', tone: 'warning', hint: 'Окно перезвона ещё идёт' },
  lost: {
    label: 'Потерян',
    tone: 'danger',
    hint: 'Окно закрылось, никто не перезвонил и никто не отметил',
  },
  recovered: {
    label: 'Вернулся',
    tone: 'success',
    hint: 'Состоялся разговор с тем же человеком',
  },
  manual: { label: 'Отработан', tone: 'neutral', hint: 'Закрыт отметкой человека' },
};

export const recoveryKind: Record<RecoveryKind, Term> = {
  client_called_back: {
    label: 'Перезвонил сам',
    tone: 'neutral',
    hint: 'Везение, а не работа колл-центра',
  },
  we_called_back: { label: 'Перезвонили мы', tone: 'success', hint: 'Работа колл-центра' },
};

export const missedActionKind: Record<MissedActionKind, Term> = {
  called_back: { label: 'Перезвонили', tone: 'success' },
  messenger: { label: 'Написали в мессенджер', tone: 'neutral' },
  client_visited: { label: 'Клиент приехал сам', tone: 'neutral' },
  no_need: { label: 'Вопрос снялся', tone: 'muted' },
  other: { label: 'Другое', tone: 'muted' },
};

/** Причины пропуска: что случилось и что с этим делать. */
export const missedReason: Record<
  keyof MissedReason,
  { label: string; fix: string; tone: Term['tone'] }
> = {
  greeting: {
    label: 'Бросил на приветствии',
    fix: 'Укоротить приветствие',
    tone: 'neutral',
  },
  waiting: {
    label: 'Не дождался ответа',
    fix: 'Мало людей в смене',
    tone: 'warning',
  },
  noAnswer: { label: 'Не взяли трубку', fix: 'Вопрос дисциплины', tone: 'warning' },
  noRoute: {
    label: 'Звонить было некому',
    fix: 'Чинить настройку АТС',
    tone: 'danger',
  },
  voicemail: { label: 'Голосовая почта', fix: 'Разобрать сообщения', tone: 'neutral' },
  unclassified: { label: 'Исход неизвестен', fix: '—', tone: 'muted' },
};

/* ------------------------------------------------------------------ */
/* Исходящие интеграции                                                 */
/* ------------------------------------------------------------------ */

/**
 * Состояние интеграции.
 *
 * `paused` — **не ошибка**: доставки копятся и поедут, когда снимут паузу.
 * Поэтому нейтральный тон, а не danger: выключенная и приостановленная
 * интеграции ведут себя по-разному, и путать их нельзя.
 */
export const integrationStatus: Record<IntegrationStatus, Term> = {
  active: { label: 'Работает', tone: 'success' },
  paused: {
    label: 'На паузе',
    tone: 'neutral',
    hint: 'Доставки копятся и поедут, когда снимут паузу',
  },
  disabled: { label: 'Выключена', tone: 'muted', hint: 'Ничего не отправляется и не копится' },
};

export const integrationAuthType: Record<IntegrationAuthType, Term> = {
  none: { label: 'Без авторизации', tone: 'muted' },
  bearer: { label: 'Токен (Bearer)', tone: 'neutral' },
  basic: {
    label: 'Логин и пароль (Basic)',
    tone: 'neutral',
    hint: 'Секрет — строка «логин:пароль» целиком: у чужих систем это одна настройка',
  },
  hmac: { label: 'Подпись тела (HMAC)', tone: 'neutral' },
  oauth2: { label: 'OAuth2', tone: 'neutral' },
};

/**
 * События, на которые подписывают интеграцию.
 *
 * `published: false` — бэкенд событие пока не публикует. Подписка сохранится и
 * заработает сама, но пометить это честнее, чем молча показать переключатель,
 * от которого ничего не поедет.
 */
export const outboundEvent: Record<OutboundEventType, Term & { published: boolean }> = {
  'call.logged': {
    label: 'Звонок состоялся',
    tone: 'neutral',
    published: true,
    hint: 'Полный журнал звонков в CRM: уезжает сразу на приёме звонка, не дожидаясь разбора. Без него в журнал не попадают звонки, разбор которых не удался или ещё не начался',
  },
  'call.completed': {
    label: 'Звонок разобран',
    tone: 'neutral',
    published: true,
    hint: 'Журнал звонков и резюме к сделке. Фильтр здесь по умолчанию пустой: дырявый журнал хуже отсутствующего',
  },
  'call.scored': {
    label: 'Звонок оценён',
    tone: 'muted',
    published: false,
    hint: 'Событие не наступает и снято: оценка приезжает вместе с «Звонок разобран»',
  },
  'call.critical_violation': {
    label: 'Критическое нарушение',
    tone: 'danger',
    published: true,
    hint: 'Задача менеджеру. Фильтр здесь главный инструмент: без него менеджер получит десяток задач в день',
  },
  'call.missed': {
    label: 'Пропущенный звонок',
    tone: 'warning',
    published: true,
    hint: 'Рождается на приёме звонка, разбора не ждёт: задача менеджеру нужна сразу, пока клиента ещё можно догнать',
  },
  'call.recovered': {
    label: 'Пропущенный доработали',
    tone: 'success',
    published: true,
    hint: 'Рождается на отметке в рабочем списке пропущенных и закрывает задачу в CRM',
  },
  'review.created': {
    label: 'Звонок разобрали',
    tone: 'neutral',
    published: true,
    hint: 'Один раз на звонок — когда человек впервые поставил звёзды или поправил вердикт правила. Последующие правки события не порождают. Самих звёзд наружу не уходит',
  },
  'comment.created': { label: 'Комментарий куратора', tone: 'neutral', published: true },
};

/**
 * Статус доставки.
 *
 * Главное здесь — **разница между `failed` и `dropped`**: первое пройдёт само
 * повтором, второе не пройдёт никогда. Одинаковый цвет означал бы либо
 * напрасную тревогу, либо спрятанную проблему. Подсказка говорит, что делать
 * человеку, а не пересказывает название.
 */
export const deliveryStatus: Record<DeliveryStatus, Term> = {
  pending: { label: 'Ждёт отправки', tone: 'muted', hint: 'Делать ничего не нужно' },
  sending: { label: 'Отправляется', tone: 'info', hint: 'Делать ничего не нужно' },
  delivered: { label: 'Доставлено', tone: 'success' },
  failed: {
    label: 'Не удалось',
    tone: 'warning',
    hint: 'Будет повтор — можно подождать',
  },
  dropped: {
    label: 'Отказ окончательный',
    tone: 'danger',
    hint: 'Повтора не будет: надо разобраться в причине',
  },
};

/**
 * Шаги хореографии адаптера.
 *
 * Словарь внутренний, и **переводить его не надо** — по нему ищут в логах
 * бэкенда, — но подписать по-русски рядом стоит. Незнакомый шаг показываем как
 * есть: новый адаптер принесёт свои, и прятать их нельзя.
 */
export const deliveryOp: Record<string, string> = {
  'call.journal': 'запись в журнале CRM',
  'summary.attach': 'резюме к сделке',
  'violation.task': 'задача менеджеру',
  'call.close': 'закрытие задачи',
};

/* ------------------------------------------------------------------ */
/* Коллтрекинг                                                          */
/* ------------------------------------------------------------------ */

/**
 * Занятость номера.
 *
 * `held` — не ошибка и не простой: номер работает прямо сейчас. Красить
 * «занято» тревожным нельзя — тревожно как раз обратное, когда свободных не
 * осталось вовсе.
 */
export const ctNumberState: Record<CtNumberState, Term> = {
  free: { label: 'Свободен', tone: 'success', hint: 'Готов к выдаче посетителю' },
  held: { label: 'Выдан', tone: 'info', hint: 'Показан посетителю прямо сейчас' },
  quarantine: {
    label: 'Карантин',
    tone: 'neutral',
    hint: 'Только что освободился и ждёт: звонок с опозданием должен найти свой переход',
  },
  static: {
    label: 'Закреплён',
    tone: 'muted',
    hint: 'Привязан к месту размещения, в подмене не участвует',
  },
  reserved: { label: 'Выведен', tone: 'muted', hint: 'Снят с оборота руками' },
};

/**
 * Как делится пул.
 *
 * Разница не косметическая, поэтому подсказка говорит про последствие, а не
 * пересказывает название.
 */
export const poolSharing: Record<PoolSharing, Term> = {
  single_site: {
    label: 'Один лендинг',
    tone: 'neutral',
    hint: 'Точнее: звонок на номер пула относится к этому лендингу даже тогда, когда переход не нашёлся',
  },
  shared: {
    label: 'Общий',
    tone: 'neutral',
    hint: 'Экономит номера, но у звонка без найденного перехода сайт назвать нечем',
  },
};

/**
 * Насколько надёжно определён переход.
 *
 * Показывать обязательно, а не только источник: кампания по «повторному
 * звонку» и по уверенному совпадению — разной надёжности, и решение на них
 * строить одинаковое нельзя. Три нижних — приглушённо.
 */
export const ctMatchKind: Record<CtMatchKind, Term> = {
  dynamic: { label: 'Переход определён', tone: 'success' },
  dynamic_quarantine: {
    label: 'Определён с оговоркой',
    tone: 'warning',
    hint: 'Номер к моменту звонка уже освободился: скорее всего тот же переход, но гарантии нет',
  },
  static: {
    label: 'Закреплённый номер',
    tone: 'neutral',
    hint: 'Номер привязан к месту размещения — подмены здесь нет',
  },
  repeat_caller: {
    label: 'По прошлому звонку',
    tone: 'muted',
    hint: 'Источник взят с прошлого звонка этого человека, до 90 дней',
  },
  unmatched: {
    label: 'Переход не нашёлся',
    tone: 'muted',
    hint: 'Звонок на рекламный номер, но переход к нему не сопоставлен',
  },
};
